import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { hasConsentChoice, updateConsent } from "@/lib/consent";
import { useLanguage } from "@/hooks/useLanguage";

const t = {
  fr: {
    title: "Respect de votre vie privée",
    description:
      "Nous utilisons des cookies analytiques (Google Analytics) pour comprendre comment vous utilisez notre site et améliorer votre expérience. Aucune donnée publicitaire n'est collectée.",
    accept: "Accepter",
    refuse: "Refuser",
    privacy: "Politique de confidentialité",
  },
  en: {
    title: "Your privacy matters",
    description:
      "We use analytics cookies (Google Analytics) to understand how you use our site and improve your experience. No advertising data is collected.",
    accept: "Accept",
    refuse: "Decline",
    privacy: "Privacy Policy",
  },
};

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const { language } = useLanguage();
  const labels = t[language] || t.fr;

  useEffect(() => {
    // Small delay so the banner doesn't flash before hydration
    const timer = setTimeout(() => {
      if (!hasConsentChoice()) setVisible(true);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const handle = (choice: "accept" | "refuse") => {
    updateConsent(choice);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-0 inset-x-0 z-[9999] p-4 md:p-6"
          role="dialog"
          aria-label={labels.title}
        >
          <div className="mx-auto max-w-2xl rounded-2xl border border-border bg-card/95 backdrop-blur-lg shadow-xl p-5 md:p-6">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <Shield className="h-5 w-5 text-primary" />
              </div>

              <div className="flex-1 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-foreground">
                    {labels.title}
                  </h3>
                  <button
                    onClick={() => handle("refuse")}
                    className="text-muted-foreground hover:text-foreground transition-colors -mr-1"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <p className="text-xs leading-relaxed text-muted-foreground">
                  {labels.description}
                </p>

                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    size="sm"
                    onClick={() => handle("accept")}
                    className="text-xs"
                  >
                    {labels.accept}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handle("refuse")}
                    className="text-xs"
                  >
                    {labels.refuse}
                  </Button>
                  <a
                    href="/privacy"
                    className="text-xs text-muted-foreground underline hover:text-foreground transition-colors"
                  >
                    {labels.privacy}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
